<!--
  @component ReleasesAggregate — cross-project Releases (scope = all / many).
  tier:   deployments aggregate view

  A per-project status board + a merged recent-deploys timeline across the
  current scope. Real data: the per-project rollup + humanised feed over the
  scoped deploy-event stream. Click a project → drill into its Releases.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import {
    projectRollup,
    feedEntries,
    relTime,
    initial,
    projectGradient,
    type ProjectStatus,
    type FeedTone,
  } from "../../home/home-util.js";
  import { scopeProjects, scopeEvents } from "./fleet-util.js";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const projects = $derived(scopeProjects(identity.projects, scope.projectIds));
  const events = $derived(scopeEvents(provenance.events, scope.projectIds));
  const roll = $derived(projectRollup(events));

  interface Row {
    readonly id: string;
    readonly name: string;
    readonly status: ProjectStatus;
    readonly lastMs: number;
  }
  const rows = $derived.by<Row[]>(() =>
    projects
      .map((p) => {
        const r = roll.get(p.id);
        return {
          id: p.id,
          name: p.name,
          status: r?.status ?? "idle",
          lastMs: r?.lastActivityMs ?? p.createdAtMs,
        } satisfies Row;
      })
      .sort((a, b) => b.lastMs - a.lastMs),
  );
  const timeline = $derived(feedEntries(events, 24));

  const STATUS_LABEL: Record<ProjectStatus, string> = {
    live: "Live", building: "Deploying", error: "Issue", idle: "Idle",
  };
  const TONE: Record<FeedTone, string> = { ok: "ok", info: "info", warn: "warn", error: "error" };

  function nameOf(pid: string | null): string | null {
    return pid ? (identity.projects.find((p) => p.id === pid)?.name ?? null) : null;
  }
  // Drill into one project's releases — sets scope={id}, navigating here with ?p=id.
  function open(id: string): void { scope.setProjects([id]); }
</script>

<div class="agg">
  <header>
    <h1>Releases</h1>
    <p class="lede">Deploy status and recent releases across {projects.length} project{projects.length === 1 ? "" : "s"}.</p>
  </header>

  <div class="cols">
    <section class="panel">
      <h2>By project</h2>
      {#if rows.length === 0}
        <p class="empty">No projects in scope.</p>
      {:else}
        <div class="board">
          {#each rows as r (r.id)}
            <button type="button" class="row" onclick={() => open(r.id)}>
              <span class="mark" style="background: {projectGradient(r.id)}">{initial(r.name)}</span>
              <span class="name" title={r.name}>{r.name}</span>
              <span class="status {r.status}">{STATUS_LABEL[r.status]}</span>
              <span class="time">{relTime(r.lastMs)}</span>
            </button>
          {/each}
        </div>
      {/if}
    </section>

    <section class="panel">
      <h2>Recent deploys</h2>
      {#if timeline.length === 0}
        <p class="empty">No deploy activity yet.</p>
      {:else}
        <ul class="feed">
          {#each timeline as e (e.id)}
            <li class="evt">
              <span class="tone {TONE[e.tone]}"></span>
              <span class="label">{e.label}</span>
              {#if nameOf(e.pid)}<span class="proj">{nameOf(e.pid)}</span>{/if}
              <span class="t">{relTime(e.atMs)}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>
  </div>
</div>

<style>
  .agg { display: flex; flex-direction: column; gap: var(--space-5); max-width: 1180px; font-family: var(--type-font); }
  header { display: flex; flex-direction: column; gap: var(--space-1); }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
  .cols { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: var(--space-4); align-items: start; }
  @media (max-width: 860px) { .cols { grid-template-columns: 1fr; } }
  .panel {
    display: flex; flex-direction: column; gap: var(--space-3);
    padding: var(--space-5); background: var(--glass-fill);
    border: 0.5px solid var(--border); border-radius: var(--radius-xl, 18px);
  }
  h2 { margin: 0; font-size: 14px; font-weight: 600; color: var(--text-primary); }
  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
  .board { display: flex; flex-direction: column; gap: 2px; }
  .row {
    display: flex; align-items: center; gap: var(--space-2);
    background: transparent; border: 0; border-radius: var(--radius-md);
    padding: 8px var(--space-2); cursor: pointer; text-align: left; font: inherit; width: 100%;
    transition: background var(--ease-fast);
  }
  .row:hover { background: var(--bg-hover); }
  .mark { width: 22px; height: 22px; border-radius: var(--radius-sm); flex-shrink: 0; display: grid; place-items: center; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.92); }
  .name { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .status { font-size: 11.5px; font-weight: 500; }
  .status.live { color: var(--success); }
  .status.building { color: var(--warning); }
  .status.error { color: var(--error); }
  .status.idle { color: var(--text-tertiary); }
  .time { font-size: 11.5px; color: var(--text-ghost); flex-shrink: 0; min-width: 56px; text-align: right; }
  .feed { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; max-height: 420px; overflow-y: auto; scrollbar-width: thin; }
  .evt { display: flex; align-items: center; gap: var(--space-2); padding: 7px 0; border-bottom: 0.5px solid var(--border-subtle); font-size: 12.5px; }
  .evt:last-child { border-bottom: 0; }
  .tone { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: var(--text-tertiary); }
  .tone.ok { background: var(--success); } .tone.warn { background: var(--warning); } .tone.error { background: var(--error); } .tone.info { background: var(--accent, #5b8cff); }
  .label { color: var(--text-primary); white-space: nowrap; }
  .proj { flex: 1; min-width: 0; color: var(--text-tertiary); font-size: 11.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .t { margin-left: auto; color: var(--text-ghost); font-size: 11.5px; flex-shrink: 0; }
  .proj + .t { margin-left: var(--space-2); }
</style>
